import BigNumber from 'bignumber.js'

import { SwapOperation } from '@blade/chart-widget/models/chart-data'
import { ChartPriceType } from '@blade/chart-widget/plugins/models/price-type'
import { Data } from '@blade/std-lib'

import type { DataHolderLimitOrder } from '@blade/chart-widget'
import type { Duration, OmitKeyDistributive } from '@blade/std-lib'
import type { TradeApi } from '@root/shared/api'

export type TargetType = 'absolute' | 'market-cap'
export const matchTargetType = Data.matchString<TargetType>()

export type Status = TradeApi.LimitOrderStatusDto
export type Type = TradeApi.LimitOrderTypeDto

export interface LimitOrderSellSettings {
  type: TargetType
  valueHigh?: BigNumber
  valueLow?: BigNumber

  /**
   * Sell amount in currency
   */
  sellAmountCurrency: BigNumber
  deadline: Duration.Duration
  expirationDate: Date
}

export interface LimitOrderBuySettings {
  type: TargetType
  valueHigh?: BigNumber
  valueLow?: BigNumber
  purchaseAmountNative: BigNumber
  deadline: Duration.Duration
  expirationDate: Date
}

export const mapFromDtoToDataHolder = (
  dto: TradeApi.LimitOrderDto,
): Array<DataHolderLimitOrder> => {
  const result: Array<DataHolderLimitOrder> = []
  const priceType =
    dto.trigger.triggerType === 'absolute'
      ? ChartPriceType.Price
      : ChartPriceType.MarketCap
  const ticker =
    dto.type === 'buy' ? dto.pairMeta.base.symbol : dto.pairMeta.quote.symbol

  const base: OmitKeyDistributive<
    DataHolderLimitOrder,
    'operation' | 'price' | 'threshold'
  > = {
    id: dto.id,
    priceType,
    amount: BigNumber(dto.amount),
    ticker,
    network: dto.network,
    valueHigh:
      dto.trigger.valueHigh === undefined
        ? undefined
        : BigNumber(dto.trigger.valueHigh),
    valueLow:
      dto.trigger.valueLow === undefined
        ? undefined
        : BigNumber(dto.trigger.valueLow),
    walletId: dto.walletId,
  }

  if (dto.type === 'buy') {
    if (dto.trigger.valueLow) {
      result.push({
        ...base,
        operation: SwapOperation.Buy,
        price: BigNumber(dto.trigger.valueLow),
      })
    }
    if (dto.trigger.valueHigh) {
      result.push({
        ...base,
        operation: SwapOperation.Buy,
        price: BigNumber(dto.trigger.valueHigh),
      })
    }
  } else {
    if (dto.trigger.valueLow) {
      result.push({
        ...base,
        operation: SwapOperation.Sell,
        threshold: 'low',
        price: BigNumber(dto.trigger.valueLow),
      })
    }
    if (dto.trigger.valueHigh) {
      result.push({
        ...base,
        operation: SwapOperation.Sell,
        threshold: 'high',
        price: BigNumber(dto.trigger.valueHigh),
      })
    }
  }

  return result
}
